import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const GoldSilverContext = createContext();

const STORAGE_KEY = 'gold_silver_overview';
const MULTI_STATE_STORAGE_KEY = 'gold_silver_multi_state_overview';
const CITY_STORAGE_PREFIX = 'gold_silver_city_overview_';

// The upstream gold/silver price API updates the day's figures between 7 and 11 AM.
// Inside that window, refresh every 30 minutes; outside it, data cached since the
// most recent window start is considered final and is reused.
const WINDOW_START_HOUR = 7;
const WINDOW_END_HOUR = 11;
const POLL_INTERVAL_MS = 30 * 60 * 1000;
// Cache must expire slightly before the next poll tick, otherwise a fetch that
// completes just after mount leaves the cache a few ms "fresh" at every tick and
// the effective refresh rate halves to once an hour.
const CACHE_TTL_MS = POLL_INTERVAL_MS - 60 * 1000;

const isWithinFetchWindow = (date = new Date()) => {
  const hour = date.getHours();
  return hour >= WINDOW_START_HOUR && hour < WINDOW_END_HOUR;
};

const isCacheFresh = (timestamp) => {
  if (!timestamp) return false;
  const now = new Date();

  if (isWithinFetchWindow(now)) {
    return now.getTime() - timestamp < CACHE_TTL_MS;
  }

  const latestWindowStart = new Date(now);
  latestWindowStart.setHours(WINDOW_START_HOUR, 0, 0, 0);
  if (now < latestWindowStart) {
    latestWindowStart.setDate(latestWindowStart.getDate() - 1);
  }
  return timestamp >= latestWindowStart.getTime();
};

const readDatedCache = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isCacheFresh(parsed?.timestamp) ? parsed.data : null;
  } catch {
    return null;
  }
};

const readCache = () => readDatedCache(STORAGE_KEY);
const readCityOverviewCache = (city) => readDatedCache(`${CITY_STORAGE_PREFIX}${city.toLowerCase()}`);
const readMultiStateCache = () => readDatedCache(MULTI_STATE_STORAGE_KEY);

export function GoldSilverProvider({ children }) {
  // Start empty (matches the server render); cached values are read in the mount effect
  // below, otherwise client-only localStorage data causes a hydration mismatch.
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cityOverview, setCityOverview] = useState(null);
  const [cityLoading, setCityLoading] = useState(false);
  const [multiStateOverview, setMultiStateOverview] = useState(null);
  const [multiStateLoading, setMultiStateLoading] = useState(false);

  const setOverviewData = useCallback((data) => {
    setOverview(data);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
      } catch (err) {
        console.error('gold silver overview cache error', err);
      }
    }
  }, []);

  const setCityOverviewData = useCallback((city, data) => {
    setCityOverview({ city, data });
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(`${CITY_STORAGE_PREFIX}${city.toLowerCase()}`, JSON.stringify({ timestamp: Date.now(), data }));
      } catch (err) {
        console.error('gold silver city overview cache error', err);
      }
    }
  }, []);

  const setMultiStateOverviewData = useCallback((data) => {
    setMultiStateOverview(data);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(MULTI_STATE_STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
      } catch (err) {
        console.error('gold silver multi state overview cache error', err);
      }
    }
  }, []);

  const loadOverview = useCallback(async () => {
    const cached = readCache();
    if (cached) {
      setOverview(cached);
      return cached;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/gold-silver-overview');
      const result = await res.json();
      if (result?.data) {
        setOverviewData(result.data);
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('gold silver overview fetch error', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setOverviewData]);

  const loadCityOverview = useCallback(async (city) => {
    if (!city) return null;
    const cached = readCityOverviewCache(city);
    if (cached) {
      setCityOverview({ city, data: cached });
      return cached;
    }

    setCityLoading(true);
    try {
      const res = await fetch(`/api/gold-silver-city-overview?city=${encodeURIComponent(city)}`);
      const result = await res.json();
      if (result?.data) {
        setCityOverviewData(city, result.data);
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('gold silver city overview fetch error', err);
      return null;
    } finally {
      setCityLoading(false);
    }
  }, [setCityOverviewData]);

  const loadMultiStateOverview = useCallback(async () => {
    const cached = readMultiStateCache();
    if (cached) {
      setMultiStateOverview(cached);
      return cached;
    }

    setMultiStateLoading(true);
    try {
      const res = await fetch('/api/gold-silver-multi-state-overview');
      const result = await res.json();
      if (result?.data) {
        setMultiStateOverviewData(result.data);
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('gold silver multi state overview fetch error', err);
      return null;
    } finally {
      setMultiStateLoading(false);
    }
  }, [setMultiStateOverviewData]);

  useEffect(() => {
    const cachedMultiState = readMultiStateCache();
    if (cachedMultiState) setMultiStateOverview(cachedMultiState);
    if (!overview && !loading) {
      loadOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Long-lived client-side sessions never remount this provider, so without this
  // the 7–11 AM refresh rule above would only ever be checked once per full page
  // load. Polls only inside that window, and only for data types a page has
  // actually requested.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isWithinFetchWindow()) return;
      loadOverview();
      if (cityOverview?.city) loadCityOverview(cityOverview.city);
      if (multiStateOverview) loadMultiStateOverview();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [cityOverview, multiStateOverview, loadOverview, loadCityOverview, loadMultiStateOverview]);

  return (
    <GoldSilverContext.Provider value={{ overview, loading, loadOverview, setOverviewData, cityOverview, cityLoading, loadCityOverview, multiStateOverview, multiStateLoading, loadMultiStateOverview }}>
      {children}
    </GoldSilverContext.Provider>
  );
}

export const useGoldSilver = () => useContext(GoldSilverContext);
